import { AppButton } from '@clickguard/design-system'
import { ChangeEvent, useState, useMemo } from 'react'
import { GInput } from 'components/basic-blocks'
import { AdSpendDropdown } from 'components/ad-spend-dropdown'
import { OnboardingLayout } from 'components/onboarding-layout'
import { isWorkEmail, extractDomainFromEmail } from 'utils/email-domains'
import { isValidDomain } from 'utils'
import { WebsiteSpendStepProps } from './types'

function getInitialWebsiteUrl(userEmail?: string, isAgency?: boolean): string {
	if (isAgency) return ''
	if (!userEmail) return ''
	if (!isWorkEmail(userEmail)) return ''
	return extractDomainFromEmail(userEmail)
}

export const WebsiteSpendStep = ({
	onSubmit,
	userEmail,
	isAgency,
	isNewWorkspace,
	loading
}: WebsiteSpendStepProps) => {
	const initialUrl = useMemo(
		() => getInitialWebsiteUrl(userEmail, isAgency),
		[userEmail, isAgency]
	)

	const [websiteUrl, setWebsiteUrl] = useState(initialUrl)
	const [adSpend, setAdSpend] = useState<number | null>(null)
	const [customSpend, setCustomSpend] = useState(0)
	const [workspaceName, setWorkspaceName] = useState('')
	const [workspaceNameError, setWorkspaceNameError] = useState<
		string | undefined
	>()
	const [websiteError, setWebsiteError] = useState<string | undefined>()
	const [error, setError] = useState<string | undefined>()

	const isCustomSpend = adSpend === 101

	const handleContinue = () => {
		setError(undefined)
		setWorkspaceNameError(undefined)
		setWebsiteError(undefined)
		if (isNewWorkspace && !workspaceName.trim()) {
			setWorkspaceNameError('Please enter a workspace name')
			return
		}
		if (!websiteUrl.trim()) {
			setWebsiteError('Please enter a website URL')
			return
		}
		if (!isValidDomain(websiteUrl.trim())) {
			setWebsiteError('Please enter a valid domain (e.g. example.com)')
			return
		}
		if (!adSpend) {
			setError('Please select your monthly ad spend')
			return
		}
		if (isCustomSpend && customSpend < 100000) {
			setError('Minimum ad spend is $100,000')
			return
		}
		onSubmit({
			websiteUrl: websiteUrl.trim(),
			adSpend: isCustomSpend ? Math.round(customSpend / 1000) : adSpend,
			workspaceName: isNewWorkspace ? workspaceName.trim() : undefined
		})
	}

	const handleWebsiteChange = (value: string) => {
		setWebsiteUrl(value)
		if (error) setError(undefined)
	}

	return (
		<OnboardingLayout currentStep={3} showStepper headerVariant="sign-up">
			<div
				className="bg-white rounded-3xl md:px-12 px-6 md:py-8 py-4 w-full max-w-[556px]"
				style={{
					boxShadow:
						'0px 12px 16px 0px rgba(16,24,40,0.08), 0px 4px 6px 0px rgba(16,24,40,0.03)'
				}}
			>
				<div className="flex flex-col">
					<h1 className="text-[24px] leading-[30px] lg:text-[30px] lg:leading-[36px] font-medium text-t-default font-grotesk mb-6 lg:mb-6">
						Let&apos;s get started
					</h1>

					<div className="flex flex-col md:gap-7 gap-4">
						{isNewWorkspace && (
							<div className="flex flex-col gap-[6px] w-full">
								<label className="text-[14px] leading-[18px] font-medium text-t-google-ad font-grotesk">
									Workspace name
								</label>
								<GInput
									value={workspaceName}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setWorkspaceName(e.target.value)
									}
									placeholder="My Workspace"
									className="w-full"
									inputClassName="px-3 py-2 border border-t-gray-input rounded-[8px] focus:border-[#554BFD] focus:outline-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[16px] leading-[22px] text-t-coupon font-grotesk"
									error={workspaceNameError}
									borderError={!!workspaceNameError}
								/>
							</div>
						)}

						<div className="flex flex-col">
							<div className="flex flex-col">
								<div className="flex flex-col mb-6 gap-[6px] w-full">
									<label className="text-[14px] leading-[18px] font-medium text-t-google-ad font-grotesk">
										What is the first website you wish to protect?
									</label>
									<GInput
										value={websiteUrl}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											handleWebsiteChange(e.target.value)
										}
										placeholder="www.example.com"
										className="w-full"
										inputClassName="px-3 py-2 border border-t-gray-input rounded-[8px] focus:border-[#554BFD] focus:outline-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] text-[16px] leading-[22px] text-t-coupon font-grotesk"
										error={websiteError}
										borderError={!!websiteError}
									/>
								</div>

								<AdSpendDropdown
									label="What is the monthly ad spend (USD) for this website?"
									value={adSpend}
									onChange={(v) => {
										setAdSpend(v)
										setError(undefined)
									}}
									onCustomSpendChange={(v) => {
										setCustomSpend(v)
										setError(undefined)
									}}
									error={error}
								/>
							</div>

							<AppButton
								variant="marketing"
								size="lg"
								fullWidth
								label="Continue"
								isLoading={loading}
								loader="dots"
								disabled={loading || !websiteUrl.trim() || !adSpend}
								onClick={handleContinue}
							/>
						</div>
					</div>
				</div>
			</div>
		</OnboardingLayout>
	)
}
